import logo from '../../assets/Logo.png';
import PitchFormImage from '../../assets/PitchFormImage.svg';
import { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import stopImage from '../../assets/stopImage.svg';

import './index.scss';
import { Button, UploadFile } from 'antd';
import { useCheckTenderPitchQuery, useSubmitPitchMutation, useManagePitchMutation } from '../../api/tenders';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader } from '../../components/Loader/Loader';
import { WithdrawModal } from './WithdrawModal';
import { ParticipationWithdrawnSuccess } from './success-screens/ParticipationWithdrawnSuccess';
import { PitchSlotConfirmedSuccess } from './PitchSlotConfirmedSuccess';
import { PreferencesSubmittedSuccess } from './success-screens/PreferencesSubmittedSuccess';
import { NewPitchSlotsProposed, type ProposedPitchSlot } from './screens/NewPitchSlotsProposed';
import { RequestNewSlot } from './RequestNewSlot';
import { SupplierPitchFormSteps, getSlotKey, type ParticipantI, type TimeSlot } from './SupplierPitchFormSteps';
import { RequestNewSlotScreen } from './RequestNewSlotScreen';
import { RequsetNewSlotSuccess } from './success-screens/RequsetNewSlotSuccess';

export const SupplierCompanyPitches = () => {
    // ================= ROUTING =================

    const [searchParams] = useSearchParams();
    const signature = searchParams.get('signature');
    const action = searchParams.get('action');
    const pitchQueryString = searchParams.toString();
    const navigate = useNavigate();

    // ================= API =================

    const { data, error, isError } = useCheckTenderPitchQuery(pitchQueryString, {
        skip: pitchQueryString.length === 0
    });
    const [submitPitch, { isSuccess, isLoading }] = useSubmitPitchMutation();
    const [withdrawPitch, { isSuccess: isSuccessWithdraw, isLoading: isLoadingWithdraw }] = useManagePitchMutation();

    // ================= UI STATE =================

    const [activeStep, setActiveStep] = useState(1);

    const [withdrawModal, setWithDrawModal] = useState(false);
    const [requestNewSlotModal, setRequestNewSlotModal] = useState(false);

    const [fileError, setFileError] = useState(false);
    const [draggedSlotKey, setDraggedSlotKey] = useState<string | null>(null);
    const [dragOverSlotKey, setDragOverSlotKey] = useState<string | null>(null);
    const [slotReorderPulseKey, setSlotReorderPulseKey] = useState<string | null>(null);
    const draggedSlotKeyRef = useRef<string | null>(null);
    const reorderPulseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // ================= DATA STATE =================

    const [slots, setSlots] = useState<TimeSlot[]>(data?.data?.slots);
    const [participantsData, setParticipantsData] = useState<ParticipantI[]>([]);
    const [file, setFile] = useState<UploadFile[]>([]);
    const [errors, setErrors] = useState<number[]>([]);
    const [newSlot, setNewSlot] = useState('');
    const [withdrawReason, setWithdrawReason] = useState('');
    const [selectedProposedSlotIndex, setSelectedProposedSlotIndex] = useState(0);
    const [pitchSlotConfirmSuccess, setPitchSlotConfirmSuccess] = useState<{
        dateLabel: string;
        timeRange: string;
    } | null>(null);

    // ================= EFFECTS =================

    useEffect(() => {
        if (isError && error) {
            navigate('/expired');
        }
    }, [error, isError]);

    useEffect(() => {
        if (data) {
            setSlots(data?.data?.slots);
        }
    }, [data]);

    const proposedPitchSlots: ProposedPitchSlot[] = useMemo(() => {
        const raw = data?.data?.slots as TimeSlot[] | undefined;
        if (!raw?.length) return [];
        return raw.map((s, i) => ({
            label: `Slot ${i + 1}`,
            dateLabel: s.date ? moment(s.date).format('DD MMM YYYY') : '',
            timeRange: [s.time_from, s.time_to].filter(Boolean).join(' - ')
        }));
    }, [data?.data?.slots]);

    useEffect(() => {
        const len = proposedPitchSlots.length;
        if (len > 0 && selectedProposedSlotIndex >= len) {
            setSelectedProposedSlotIndex(0);
        }
    }, [proposedPitchSlots.length, selectedProposedSlotIndex]);

    useEffect(() => {
        return () => {
            if (reorderPulseTimerRef.current) clearTimeout(reorderPulseTimerRef.current);
        };
    }, []);

    // ================= SLOT DRAG / REORDER =================

    const moveSlot = (fromSlotKey: string, toSlotKey: string, opts?: { pulse?: boolean }) => {
        setSlots((prev) => {
            const fromIndex = prev.findIndex((s) => getSlotKey(s) === fromSlotKey);
            const toIndex = prev.findIndex((s) => getSlotKey(s) === toSlotKey);
            if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return prev;

            const next = [...prev];
            const [removed] = next.splice(fromIndex, 1);
            next.splice(toIndex, 0, removed);
            return next;
        });

        if (opts?.pulse) {
            setSlotReorderPulseKey(fromSlotKey);
            if (reorderPulseTimerRef.current) clearTimeout(reorderPulseTimerRef.current);
            reorderPulseTimerRef.current = setTimeout(() => {
                setSlotReorderPulseKey(null);
                reorderPulseTimerRef.current = null;
            }, 480);
        }
    };

    const handleDragStart = (slotKey: string) => {
        draggedSlotKeyRef.current = slotKey;
        setDraggedSlotKey(slotKey);
        setDragOverSlotKey(null);
    };

    const handleDragOver = (e: React.DragEvent, slotKey: string) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        const fromKey = draggedSlotKeyRef.current;
        if (fromKey !== null && fromKey !== slotKey) {
            setDragOverSlotKey(slotKey);
            moveSlot(fromKey, slotKey);
        }
    };

    const handleDragEnd = () => {
        draggedSlotKeyRef.current = null;
        setDraggedSlotKey(null);
        setDragOverSlotKey(null);
    };

    // ================= PARTICIPANTS =================

    const handleInputChange = (index: number, value: string, field: keyof ParticipantI) => {
        setParticipantsData((prev) => {
            const updated = [...prev];

            if (!updated[index]) {
                updated[index] = {
                    title: '',
                    full_name: ''
                };
            }

            updated[index] = {
                ...updated[index],
                [field]: value
            };

            return updated;
        });

        setErrors((prev) => prev.filter((i) => i !== index));
    };

    // ================= VALIDATION =================

    const handleSave = () => {
        const newErrors: number[] = [];

        const count = data?.data?.representatives_count || 0;

        for (let i = 0; i < count; i++) {
            const participant = participantsData[i];

            if (!participant || !participant.title?.trim() || !participant.full_name?.trim()) {
                newErrors.push(i);
            }
        }

        setErrors(newErrors);

        if (newErrors.length === 0) {
            setActiveStep(3);
        }
    };

    // ================= SUBMIT =================

    const buildPitchFormData = () => {
        const formData = new FormData();

        slots.forEach((slot, index) => {
            formData.append(`slots[${index}][date]`, slot.date);
            formData.append(`slots[${index}][time_from]`, slot.time_from);
            formData.append(`slots[${index}][time_to]`, slot.time_to);
        });

        participantsData?.forEach((participant, index) => {
            formData.append(`participants[${index}][title]`, participant.title);
            formData.append(`participants[${index}][name]`, participant.full_name);
        });

        formData.append('status', newSlot ? 'slots_request' : 'submitted');

        if (newSlot) {
            formData.append('reason', newSlot);
        }

        if (file?.length) {
            const fileBlob: any = file?.[0].originFileObj;
            formData.append('file', fileBlob);
        }

        return formData;
    };

    const handleSubmit = () => {
        setPitchSlotConfirmSuccess(null);
        if (withdrawReason) {
            const formData = {
                signature: signature,
                data: {
                    reason: withdrawReason,
                    status: 'declined'
                }
            };
            withdrawPitch(formData);
        } else if (newSlot) {
            submitPitch({
                signature: signature,
                data: buildPitchFormData()
            });
        } else {
            submitPitch({
                signature: signature,
                data: buildPitchFormData()
            });
        }
    };

    // ================= OTHER =================

    const requestNewSlot = (reason: string) => {
        if (!signature) return;

        setNewSlot(reason);
        setActiveStep(2);
    };

    const requestWithdraw = (reason: string) => {
        setWithdrawReason(reason);
        setWithDrawModal(false);
        setActiveStep(5);
    };

    return (
        <>
            <div className="SupplierCompanyPitches">
                <div className="SupplierCompanyPitches__logowrap">
                    <img src={logo} className="SupplierCompanyPitches__logo" />
                </div>

                {data?.data?.status === 'reschedule' ? (
                    <NewPitchSlotsProposed data={data?.data} />
                ) : isSuccessWithdraw ? (
                    <ParticipationWithdrawnSuccess />
                ) : isSuccess ? (
                    newSlot ? (
                        <RequsetNewSlotSuccess />
                    ) : (
                        <PreferencesSubmittedSuccess />
                    )
                ) : action === 'request_new_slots' ? (
                    <RequestNewSlotScreen data={data?.data} />
                ) : false ? (
                    <PitchSlotConfirmedSuccess
                        dateLabel={pitchSlotConfirmSuccess?.dateLabel ?? ''}
                        timeRange={pitchSlotConfirmSuccess?.timeRange ?? ''}
                    />
                ) : (
                    <div className="SupplierCompanyPitches__main">
                        <div className="supplier-pitch">
                            <div className="supplier-pitch__content">
                                <div className="supplier-pitch__wrapper">
                                    <img src={PitchFormImage} className="SupplierCompanyPitches__image-mobile" />
                                    <header className="supplier-pitch__header">
                                        <h1 className="supplier-pitch__title">Supplier Pitch</h1>
                                        <p className="supplier-pitch__greeting">
                                            Hello, <strong>{data?.data?.company?.name}</strong>. Please set your pitch
                                            slot preferences and submit the required information below.
                                        </p>
                                        <div className="supplier-pitch__tags">
                                            <span className="supplier-pitch__tag">{data?.data?.bayer?.name}</span>
                                            <span className="supplier-pitch__tag">{data?.data?.expense_area_name}</span>
                                        </div>
                                    </header>
                                </div>

                                <div className="supplier-pitch__divider" />

                                {withdrawReason ? (
                                    <section className="supplier-pitch__section">
                                        <div className="supplier-pitch__section-header">
                                            <span className="supplier-pitch__section-number inactive">1</span>
                                            <h2 className="supplier-pitch__section-title">
                                                Select Your Preferred Pitch Slots
                                            </h2>
                                        </div>
                                        <div className="supplier-pitch__newSlot">
                                            <div className="supplier-pitch__newSlot-title">
                                                {' '}
                                                <div className="slot-value error">
                                                    <img src={stopImage} alt="calendar" />
                                                    Participation Withdrawn
                                                </div>
                                                <Button
                                                    onClick={() => {
                                                        setWithdrawReason('');
                                                        setActiveStep(1);
                                                    }}
                                                >
                                                    Cancel Withdrawal
                                                </Button>
                                            </div>
                                            <div className="supplier-pitch__reason">
                                                <h1>Reason for Refusal</h1>
                                                <div>{withdrawReason}</div>
                                            </div>
                                        </div>
                                    </section>
                                ) : (
                                    <SupplierPitchFormSteps
                                        activeStep={activeStep}
                                        setActiveStep={setActiveStep}
                                        newSlot={newSlot}
                                        setNewSlot={setNewSlot}
                                        slots={slots}
                                        moveSlot={moveSlot}
                                        handleDragStart={handleDragStart}
                                        handleDragOver={handleDragOver}
                                        handleDragEnd={handleDragEnd}
                                        draggedSlotKey={draggedSlotKey}
                                        dragOverSlotKey={dragOverSlotKey}
                                        slotReorderPulseKey={slotReorderPulseKey}
                                        setRequestNewSlotModal={setRequestNewSlotModal}
                                        setWithDrawModal={setWithDrawModal}
                                        representativesCount={data?.data?.representatives_count}
                                        participantsData={participantsData}
                                        handleInputChange={handleInputChange}
                                        errors={errors}
                                        handleSave={handleSave}
                                        file={file}
                                        setFile={setFile}
                                        fileError={fileError}
                                        setFileError={setFileError}
                                    />
                                )}

                                <Button
                                    type="primary"
                                    style={{ width: '100%', margin: '24px 0' }}
                                    disabled={activeStep === 1 || activeStep === 2 || activeStep === 3}
                                    onClick={() => handleSubmit()}
                                >
                                    {isLoading || isLoadingWithdraw ? <Loader /> : 'Submit Form'}
                                </Button>
                            </div>
                        </div>
                        <img src={PitchFormImage} className="SupplierCompanyPitches__image" />
                    </div>
                )}
            </div>
            {withdrawModal && (
                <WithdrawModal
                    openModal={withdrawModal}
                    setOpenModal={setWithDrawModal}
                    requestWithdraw={requestWithdraw}
                />
            )}
            {requestNewSlotModal && (
                <RequestNewSlot
                    openModal={requestNewSlotModal}
                    setOpenModal={setRequestNewSlotModal}
                    requestNewSlot={requestNewSlot}
                />
            )}
        </>
    );
};
